import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Brain } from 'lucide-react';
import { stockAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import AddToJournalDialog from './AddToJournalDialog';
import LightweightCandleChart from './LightweightCandleChart';
import ChartControls from './premium-chart/ChartControls';
import ChartIndicatorStrip from './premium-chart/ChartIndicatorStrip';
import PriceAreaChart from './premium-chart/PriceAreaChart';
import { RSIPanel, VolumePanel } from './premium-chart/RsiVolumePanels';

const PremiumChart = ({ symbol, isPro }) => {
  const { toast } = useToast();
  const [period, setPeriod] = useState('6mo');
  const [chart, setChart] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [journalOpen, setJournalOpen] = useState(false);

  useEffect(() => {
    if (!symbol) return undefined;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setErrorMsg('');
      try {
        const data = await stockAPI.getAIChart(symbol, period, isPro);
        if (!cancelled) setChart(data);
      } catch (e) {
        const status = e?.response?.status;
        const msg =
          status === 503
            ? 'Yahoo Finance is rate-limiting requests. Please retry in ~60s.'
            : `Could not load chart for ${symbol}`;
        if (!cancelled) {
          setErrorMsg(msg);
          setChart(null);
          toast({ title: 'Chart unavailable', description: msg, variant: 'destructive' });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [symbol, period, isPro, toast]);

  if (!symbol) {
    return (
      <Card data-testid="premium-chart-empty">
        <CardContent className="p-8 text-center text-gray-500">
          Search for a stock above to view the premium AI chart.
        </CardContent>
      </Card>
    );
  }

  const renderBody = () => {
    if (loading) {
      return <div className="h-80 flex items-center justify-center text-gray-500">Loading chart…</div>;
    }
    if (errorMsg) {
      return (
        <div className="h-80 flex items-center justify-center text-red-600 text-sm" data-testid="chart-error">
          {errorMsg}
        </div>
      );
    }
    if (!chart?.data?.length) {
      return <div className="h-80 flex items-center justify-center text-gray-500">No chart data</div>;
    }
    return (
      <>
        <ChartIndicatorStrip indicators={chart.indicators} />
        <LightweightCandleChart data={chart.data} />
        <PriceAreaChart data={chart.data} indicators={chart.indicators} />
        <RSIPanel data={chart.data} />
        <VolumePanel data={chart.data} />
        {chart.commentary && (
          <div
            className="mt-4 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-100 rounded-lg p-4"
            data-testid="chart-ai-commentary"
          >
            <div className="flex items-center gap-2 mb-2">
              <Brain size={16} className="text-purple-600" />
              <span className="font-semibold text-sm text-purple-900">AI Commentary</span>
            </div>
            <p className="text-sm text-gray-800 leading-relaxed">{chart.commentary}</p>
          </div>
        )}
      </>
    );
  };

  return (
    <Card className="border-purple-100" data-testid="premium-chart-card">
      <CardHeader className="pb-3">
        <ChartControls
          symbol={symbol}
          isPro={isPro}
          period={period}
          onPeriodChange={setPeriod}
          onAddJournal={() => setJournalOpen(true)}
        />
      </CardHeader>
      <CardContent>{renderBody()}</CardContent>
      <AddToJournalDialog
        open={journalOpen}
        onOpenChange={setJournalOpen}
        symbol={symbol}
        defaultEntryPrice={chart?.indicators?.lastPrice}
        defaultSide="buy"
        source="chart"
      />
    </Card>
  );
};

export default PremiumChart;
